import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { previewLabelKey } from './conversationPreview';
import { getConversations as e2eeGetConversations } from '../e2ee/conversations';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Alert,
  BackHandler,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import Ionicons from '@expo/vector-icons/Ionicons';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { formatDisplayCount } from '../i18n/formatCount';
import { api, Conversation } from '../services/api';
import VerifiedBadge from '../components/VerifiedBadge';
import { useUnread } from '../unread/UnreadContext';
import { timeAgo } from '../utils/time';
import Avatar from '../components/Avatar';
import { callLabel } from '../calls/label';
import {
  getPushPermissionState,
  PushPermissionState,
  requestPushPermission,
} from '../notifications/pushLifecycle';
import { storage } from '../services/storage';
import { CONVERSATION_MUTE_OPTIONS } from '../notifications/muteOptions';
import NotificationBellButton from '../components/NotificationBellButton';
import PageHeader from '../components/PageHeader';
import UndoSnackbar from '../components/UndoSnackbar';
import { moodTint } from '../avatarMood/tint';
import { useVersionNotice } from '../appVersion/versionNotice';
import { floatingDockContentInset, floatingDockScrollInset } from '../components/floatingDockLayout';

type Props = {
  token: string;
  myId: number;
  onSelectConversation: (userId: number, userName: string, userPhoto: string | null) => void;
  onNotificationCenter: () => void;
};

// Sohbet listesinde çağrı olayının tek satırlık önizlemesi. content ham JSON
// olduğu için parse edilemezse satır boş kalır — bozuk bir kayıt yüzünden
// gelen kutusu çökmemeli.
function callPreview(
  content: string,
  mine: boolean,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  try { return callLabel(JSON.parse(content), mine, t); } catch { return ''; }
}

function previewAuthorPrefix(label: string): string {
  return label.endsWith('：') ? label : `${label} `;
}

export default function InboxScreen({ token, myId, onSelectConversation, onNotificationCenter }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { syncFromConversations, refresh: refreshUnread, markConversationRead } = useUnread();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // 8.6 / D-667 — telefondaki derleme eskiyse "Yeni sürüm var" kartı.
  const versionNotice = useVersionNotice();
  // D-666 — sohbet açıkken bu cihazda hesaplanmış konuşma duyguları. Liste için
  // hiçbir konuşma ayrıca çekilmez; yalnız saklanan sonuç okunur.
  const [moods, setMoods] = useState<Record<number, string | null>>({});
  useEffect(() => {
    let active = true;
    storage.getAvatarMoods(myId, conversations.map((c) => c.other_id))
      .then((next) => { if (active) setMoods(next); })
      .catch(() => {});
    return () => { active = false; };
  }, [myId, conversations]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedUnread, setArchivedUnread] = useState(false);
  const [mutatingIds, setMutatingIds] = useState<Set<number>>(() => new Set());
  const mutatingIdsRef = useRef<Set<number>>(new Set());
  const [undo, setUndo] = useState<{ text: string; action: () => Promise<void> } | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pushPermission, setPushPermission] = useState<PushPermissionState>('unsupported');
  const [pushPromptDismissed, setPushPromptDismissed] = useState(true);

  const load = useCallback(async (archived: boolean) => {
    setLoading(true);
    setFailed(false);
    try {
      // Şifreli satırın önizlemesi yerel kopyadan gelir (9.34 · D-617).
      const list = await e2eeGetConversations(token, archived);
      setConversations(list);
      // Liste zaten elimizde; dock rozetini ikinci bir istek atmadan besle
      // (9.26.7.5 — sayacın tek kaynağı). Arşiv görünümü toplama girmiyor.
      if (!archived) syncFromConversations(list);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
    api.getArchivedUnread(token)
      .then((r) => setArchivedUnread(r.hasUnread))
      .catch(() => {});
  }, [token, syncFromConversations]);

  useEffect(() => {
    load(false);
    return () => { if (undoTimer.current) clearTimeout(undoTimer.current); };
  }, [load]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getPushPermissionState(),
      storage.getNotificationPromptDismissed(myId),
      // 9.26 / D-637 — bildirimler varsayılan olarak KAPALI. Hesapta kapalıyken
      // cihaz iznini istemek, kişiye işe yaramayan bir şeyi açtırmak olurdu:
      // izni verir ve yine hiçbir bildirim almaz. Kart ancak hesapta açıkken
      // anlamlı; okunamazsa kart gösterilmiyor — yanlış bir istek, hiç
      // istememekten kötü.
      api.getNotificationPreferences(token)
        .then((p) => p.push_enabled === true)
        .catch(() => false),
    ]).then(([permission, dismissed, hesaptaAcik]) => {
      if (!active) return;
      setPushPermission(permission);
      setPushPromptDismissed(dismissed || !hesaptaAcik);
    });
    return () => { active = false; };
  }, [myId, token]);

  async function dismissPushPrompt() {
    setPushPromptDismissed(true);
    await storage.dismissNotificationPrompt(myId);
  }

  async function enablePushNotifications() {
    // Sistem istemi kapatılsa bile kart otomatik tekrar tekrar gösterilmez;
    // Profil'deki kalıcı Bildirimler satırı her zaman erişilebilir kalır.
    await dismissPushPrompt();
    setPushPermission(await requestPushPermission(token));
  }

  function toggleArchivedView() {
    const next = !showArchived;
    setShowArchived(next);
    setQuery('');
    load(next);
  }

  // 7.6 / D-662 — Arşiv açıkken Android'in geri tuşu önce Arşiv'i kapatır;
  // ekrandaki geri okuyla aynı yere döner (D-648: iki geri aynı kaydı izler).
  useEffect(() => {
    if (!showArchived) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      toggleArchivedView();
      return true;
    });
    return () => sub.remove();
  });

  function showUndo(text: string, action: () => Promise<void>) {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndo({ text, action });
    undoTimer.current = setTimeout(() => setUndo(null), 4000);
  }

  async function doUndo() {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    const action = undo?.action;
    setUndo(null);
    if (!action) return;
    try {
      await action();
    } catch {
      Alert.alert(t('common.error'));
    }
  }

  async function runConversationMutation(
    item: Conversation,
    operation: () => Promise<unknown>,
    onSuccess?: () => void
  ) {
    if (mutatingIdsRef.current.has(item.other_id)) return;
    mutatingIdsRef.current.add(item.other_id);
    setMutatingIds((current) => new Set(current).add(item.other_id));
    try {
      await operation();
      await load(showArchived);
      onSuccess?.();
    } catch {
      Alert.alert(t('common.error'));
    } finally {
      mutatingIdsRef.current.delete(item.other_id);
      setMutatingIds((current) => {
        const next = new Set(current);
        next.delete(item.other_id);
        return next;
      });
    }
  }

  function doArchive(item: Conversation) {
    if (showArchived) {
      runConversationMutation(item, () => api.unarchiveConversation(token, item.other_id), () => {
        showUndo(t('inbox.unarchived'), async () => {
          await api.archiveConversation(token, item.other_id);
          await load(true);
        });
      });
    } else {
      runConversationMutation(item, () => api.archiveConversation(token, item.other_id), () => {
        showUndo(t('inbox.archived'), async () => {
          await api.unarchiveConversation(token, item.other_id);
          await load(false);
        });
      });
    }
  }

  function applyMute(item: Conversation, hours?: number) {
    runConversationMutation(item, () => api.muteConversation(token, item.other_id, hours));
  }

  function doMute(item: Conversation) {
    if (item.muted) {
      runConversationMutation(item, () => api.unmuteConversation(token, item.other_id));
      return;
    }
    // Süreli sustur seçenekleri (#7) — süresiz değil, süre seçilebilir.
    Alert.alert(t('inbox.muteTitle', { name: item.other_name }), undefined, [
      ...CONVERSATION_MUTE_OPTIONS.map((option) => ({
        text: t(option.translationKey),
        onPress: () => applyMute(item, option.durationHours ?? undefined),
      })),
      { text: t('common.cancel'), style: 'cancel' },
    ]);
  }

  function doBlock(item: Conversation) {
    Alert.alert(
      t('inbox.blockTitle', { name: item.other_name }),
      t('inbox.blockMessage'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('inbox.blockConfirm'),
          style: 'destructive',
          onPress: () => {
            // Engellenen konuşma listeden düşer (backend gizler)
            runConversationMutation(item, () => api.blockUser(token, item.other_id));
          },
        },
      ]
    );
  }

  // 9.26.7.1.2 — listeden okunmadı/okundu geçişi. Mesajların read_at'i değişmiyor:
  // sunucu ayrı bir bayrak tutuyor, karşı tarafın tiki (5.4) geri alınmıyor.
  function doToggleUnread(item: Conversation) {
    const wasUnread = item.unread_count > 0 || item.manual_unread;
    const operation = wasUnread
      ? () => api.markConversationRead(token, item.other_id)
      : () => api.markConversationUnread(token, item.other_id);
    runConversationMutation(item, operation, () => {
      if (wasUnread) markConversationRead(item.other_id);
      else refreshUnread();
    });
  }

  function onLongPressRow(item: Conversation) {
    const unread = item.unread_count > 0 || item.manual_unread;
    Alert.alert(item.other_name, undefined, [
      {
        text: unread ? t('inbox.markRead') : t('inbox.markUnread'),
        onPress: () => doToggleUnread(item),
      },
      { text: item.muted ? t('inbox.unmute') : t('inbox.mute'), onPress: () => doMute(item) },
      {
        text: showArchived ? t('inbox.unarchiveAction') : t('inbox.archiveAction'),
        onPress: () => doArchive(item),
      },
      { text: t('inbox.blockAction'), style: 'destructive', onPress: () => doBlock(item) },
      { text: t('common.cancel'), style: 'cancel' },
    ]);
  }

  return (
    <View style={[styles.container, !showArchived && { paddingTop: insets.top }]}>
      {showArchived ? (
        // 7.6 / D-662 — Arşiv de ortak başlıkla açılır: geri oku solda, başlık
        // solda (D-648). Sağ üstteki "‹ Geri" yazısı D-648'in profilde kaldırdığı
        // biçimdi; burada kalmıştı.
        <PageHeader title={t('inbox.archiveTitle')} onBack={toggleArchivedView} backTestID="inbox-archive-back" />
      ) : (
        <View style={styles.headerRow}>
          <Text style={styles.header}>{''}</Text>
          <View style={styles.headerActions}>
            <NotificationBellButton onPress={onNotificationCenter} />
            <TouchableOpacity onPress={toggleArchivedView} style={styles.archiveBtn} testID="inbox-archive-open">
              {archivedUnread && <View style={styles.unreadDot} />}
              <Text style={styles.archiveToggle}>{t('inbox.archive')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      <View style={styles.searchWrap}>
        <TextInput
          style={styles.searchInput}
          placeholder={t('common.search')}
          placeholderTextColor={colors.gray400}
          value={query}
          onChangeText={setQuery}
          autoCorrect={false}
          clearButtonMode="while-editing"
        />
      </View>

      {/* 8.6 / D-667 — Bildirimler kartıyla aynı biçim; "Şimdi değil" bir gün susturur. */}
      {!showArchived && versionNotice.visible && (
        <View style={styles.notificationCard} testID="version-notice">
          <View style={styles.notificationCardText}>
            <Text style={styles.notificationCardTitle}>{t('versionNotice.title')}</Text>
            <Text style={styles.notificationCardBody}>{t('versionNotice.body')}</Text>
          </View>
          <View style={styles.notificationActions}>
            <TouchableOpacity onPress={versionNotice.snooze} style={styles.notificationSecondary} testID="version-notice-later">
              <Text style={styles.notificationSecondaryText} numberOfLines={2}>{t('notifications.notNow')}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={versionNotice.update} style={styles.notificationPrimary} testID="version-notice-update">
              <Text style={styles.notificationPrimaryText} numberOfLines={2}>{t('versionNotice.update')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {!showArchived && pushPermission === 'undetermined' && !pushPromptDismissed && (
        <View style={styles.notificationCard}>
          <View style={styles.notificationCardText}>
            <Text style={styles.notificationCardTitle}>{t('notifications.title')}</Text>
            <Text style={styles.notificationCardBody}>{t('notifications.cardBody')}</Text>
          </View>
          <View style={styles.notificationActions}>
            <TouchableOpacity onPress={dismissPushPrompt} style={styles.notificationSecondary}>
              <Text style={styles.notificationSecondaryText} numberOfLines={2}>{t('notifications.notNow')}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={enablePushNotifications} style={styles.notificationPrimary}>
              <Text style={styles.notificationPrimaryText} numberOfLines={2}>{t('notifications.enable')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.black} />
        </View>
      ) : failed ? (
        <View style={styles.center}>
          <Text style={styles.errorText}>{t('inbox.failed')}</Text>
          <CompactActionButton
              variant="primary"
              label={t('common.retry')}
              onPress={() => load(showArchived)}
              style={styles.retryBtn}
              testID="inbox-retry"
            />
        </View>
      ) : (
        <FlatList
          data={conversations.filter(c =>
            c.other_name.toLowerCase().includes(query.toLowerCase())
          )}
          keyExtractor={(item) => String(item.other_id)}
          contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + floatingDockContentInset }]}
          scrollIndicatorInsets={{ bottom: insets.bottom + floatingDockScrollInset }}
          renderItem={({ item }) => (
            <Swipeable
              friction={2}
              rightThreshold={48}
              dragOffsetFromRightEdge={40}
              overshootRight={false}
              renderRightActions={() => (
                <View testID="inbox-swipe-action" style={styles.swipeAction}>
                  <View testID="inbox-swipe-icon-surface" style={styles.swipeIcon} accessible={false}>
                    <Ionicons name={showArchived ? 'arrow-undo-outline' : 'archive-outline'} size={25}
                      color={colors.black} accessible={false} />
                  </View>
                  <Text style={styles.swipeActionText}>
                    {showArchived ? t('inbox.swipeRemove') : t('inbox.swipeArchive')}
                  </Text>
                </View>
              )}
              onSwipeableOpen={() => doArchive(item)}>
              <TouchableOpacity
                style={[styles.row, mutatingIds.has(item.other_id) && styles.rowMutating]}
                disabled={mutatingIds.has(item.other_id)}
                onPress={() => onSelectConversation(item.other_id, item.other_name, item.other_photo)}
                onLongPress={() => onLongPressRow(item)}>
                <Avatar photoUrl={item.other_photo} name={item.other_name} size={52} style={styles.avatar} tint={moodTint(moods[item.other_id])} />
                <View style={styles.info}>
                  <View style={styles.topRow}>
                    <View style={styles.nameWrap}>
                      <Text
                        style={[styles.name, (item.unread_count > 0 || item.manual_unread) && !item.muted && styles.nameUnread]}
                        numberOfLines={1}>
                        {item.other_name}
                      </Text>
                      {item.other_is_official && <VerifiedBadge size={15} />}
                      {item.muted && <Text style={styles.muteIcon}>🔕</Text>}
                    </View>
                    <Text style={[styles.time, (item.unread_count > 0 || item.manual_unread) && !item.muted && styles.timeUnread]}>
                      {timeAgo(item.last_at, t, lang)}
                    </Text>
                  </View>
                  <View style={styles.bottomRow}>
                    <Text
                      style={[styles.preview, (item.unread_count > 0 || item.manual_unread) && !item.muted && styles.previewUnread]}
                      numberOfLines={1}>
                      {item.last_type === 'call' ? '' : item.last_sender_id === myId ? previewAuthorPrefix(t('inbox.you')) : ''}{item.last_type === 'meetup' ? (() => {
                        try {
                          const data = JSON.parse(item.last_content);
                          if (data?.changeType === 'reschedule') {
                            return t(data.kind === 'call'
                              ? 'call.rescheduleBubbleShort'
                              : 'calendar.rescheduleBubbleShort');
                          }
                        } catch {}
                        return t('calendar.meetupBubbleShort');
                      })() : item.last_type === 'call' ? callPreview(item.last_content, item.last_sender_id === myId, t) : (() => {
                        // Medya ve kart türlerinde ham içerik YAZILMIYOR: orada
                        // bir depo adresi ya da JSON duruyor (D-586).
                        const etiket = previewLabelKey(item.last_type);
                        return etiket ? t(etiket) : item.last_content;
                      })()}
                    </Text>
                    {item.unread_count > 0 ? (
                      <View style={[styles.badge, item.muted && styles.badgeMuted]}>
                        <Text style={styles.badgeText}>{formatDisplayCount(item.unread_count, lang)}</Text>
                      </View>
                    ) : item.manual_unread ? (
                      // Elle işaretlenmiş sohbette gösterilecek bir sayı yok;
                      // nokta "buraya dönecektim" demek için yeterli (9.26.7.1.2).
                      <View style={[styles.badgeDot, item.muted && styles.badgeMuted]} />
                    ) : null}
                  </View>
                </View>
              </TouchableOpacity>
            </Swipeable>
          )}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={
            <Text style={styles.empty}>
              {query ? t('inbox.searchEmpty') : showArchived ? t('inbox.archivedEmpty') : t('inbox.empty')}
            </Text>
          }
        />
      )}

      {undo && (
        <UndoSnackbar text={undo.text} actionLabel={t('inbox.undo')} onAction={doUndo} bottom={insets.bottom + 90} />
      )}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  header: {
    fontSize: fontSize.lg,
    fontWeight: '700',
    color: colors.black,
  },
  archiveBtn: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  archiveToggle: { fontSize: fontSize.md, color: colors.gray600 },
  unreadDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#e53935' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  retryBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
  searchWrap: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
  searchInput: {
    backgroundColor: colors.gray100,
    borderRadius: radius.control,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: fontSize.md,
    color: colors.black,
  },
  list: { paddingHorizontal: spacing.lg },
  notificationCard: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: radius.card,
    borderWidth: 1,
    borderColor: colors.gray200,
    backgroundColor: colors.gray100,
    gap: spacing.md,
  },
  notificationCardText: { gap: 4 },
  notificationCardTitle: { color: colors.black, fontSize: fontSize.md, fontWeight: '700' },
  notificationCardBody: { color: colors.gray600, fontSize: fontSize.sm, lineHeight: 19 },
  notificationActions: { flexDirection: 'row', gap: spacing.sm },
  notificationPrimary: {
    flex: 1,
    minHeight: 44,
    borderRadius: radius.control,
    backgroundColor: colors.black,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notificationPrimaryText: { color: colors.white, fontSize: fontSize.sm, fontWeight: '700', textAlign: 'center' },
  notificationSecondary: {
    flex: 1,
    minHeight: 44,
    borderRadius: radius.control,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notificationSecondaryText: { color: colors.gray600, fontSize: fontSize.sm, fontWeight: '600', textAlign: 'center' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    backgroundColor: colors.white,
  },
  rowMutating: { opacity: 0.55 },
  swipeAction: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 96,
    paddingHorizontal: 4,
    gap: 5,
    marginVertical: 4,
  },
  swipeIcon: { width: 52, height: 52, borderRadius: 26, backgroundColor: colors.gray100, justifyContent: 'center', alignItems: 'center' },
  swipeActionText: { color: colors.gray600, fontSize: fontSize.sm, lineHeight: 18, textAlign: 'center', flexShrink: 1 },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    marginRight: spacing.md,
  },
  info: { flex: 1 },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
  },
  nameWrap: { flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: spacing.sm },
  name: { fontSize: fontSize.md, fontWeight: '600', color: colors.black, flexShrink: 1 },
  nameUnread: { fontWeight: '800' },
  muteIcon: { fontSize: 12, marginLeft: 5, opacity: 0.5 },
  time: { fontSize: fontSize.sm, color: colors.gray400 },
  timeUnread: { color: colors.black, fontWeight: '600' },
  bottomRow: { flexDirection: 'row', alignItems: 'center' },
  preview: { fontSize: fontSize.sm, color: colors.gray400, flex: 1 },
  previewUnread: { color: colors.black, fontWeight: '500' },
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: radius.control,
    paddingHorizontal: 6,
    backgroundColor: colors.black,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.sm,
  },
  badgeDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.black,
    marginLeft: spacing.sm,
  },
  badgeMuted: { backgroundColor: colors.gray400 },
  badgeText: { color: colors.white, fontSize: 12, fontWeight: '700' },
  separator: { height: 1, backgroundColor: colors.gray100 },
  empty: {
    fontSize: fontSize.md,
    color: colors.gray400,
    textAlign: 'center',
    paddingTop: spacing.xl,
  },
});
